import Tabs, { TabType } from 'components/Tabs'
import TruckMenu from 'components/pages/TruckMenuPage/TruckMenu'
import { ReactNode, useState } from 'react'
import sortBy from 'lodash/sortBy'
import { useTruckMenusQuery } from 'components/TruckMenus/types/TruckMenusQuery'
import { useMenuByIdQuery } from 'components/TruckMenus/types/MenuByIdQuery'
import { TruckMenuContentFragment } from 'components/TruckMenus/types/TruckMenuContentFragment'
import ButtonLink from 'components/ui/ButtonLink'
import Loading from 'components/Loading'
import pathFromUrl from 'utils/pathFromUrl'

type Props = {
  truckId: number
  itemIdsParam?: string
  menuIdParam?: string | number | null
  showLinkToSchedule?: boolean
  describeMenuPricing?: boolean
  /** Rendered instead of the menu when there is nothing to show. */
  fallback?: ReactNode
}
const TruckMenus = ({
  truckId,
  itemIdsParam,
  menuIdParam,
  showLinkToSchedule,
  describeMenuPricing,
  fallback = null
}: Props) => {
  let menuId: number | null = null
  if (menuIdParam && typeof menuIdParam === 'string') {
    menuId = parseInt(menuIdParam, 10)
  } else if (menuIdParam && typeof menuIdParam === 'number') {
    menuId = menuIdParam
  }
  const [currentTab, setCurrentTab] = useState<TabType>()

  const { data } = useTruckMenusQuery({
    variables: { id: truckId }
  })

  // Trucks can have dozens of menus, so the item-heavy payload is only loaded for
  // the two a customer can see, or for a specifically requested menu.
  const { data: menuData, loading: menuLoading } = useMenuByIdQuery({
    variables: { id: menuId ?? 0 },
    skip: !menuId
  })

  const truck = data?.truck
  if (!truck) return null
  if (menuId && menuLoading) {
    return (
      <div className="max-w-screen-lg mx-auto mt-10" id="menu">
        <Loading size="large" />
      </div>
    )
  }

  const itemIds: number[] | null = itemIdsParam
    ? itemIdsParam.split(',').map((i) => parseInt(i, 10))
    : null

  let menus: TruckMenuContentFragment[]
  if (menuId) {
    // A direct link reaches a private menu (proposal emails link to one), but not
    // a deleted or empty menu — and `menu(id:)` isn't scoped to a truck, so the
    // menu also has to be served by this one.
    const requestedMenu = menuData?.menu
    menus =
      requestedMenu &&
      requestedMenu.active &&
      requestedMenu.items.length > 0 &&
      requestedMenu.trucks.some((t) => t.id === truckId)
        ? [requestedMenu]
        : []
  } else {
    // Customers only see the truck's default a la carte + catering menus.
    // `public` is a catering-only flag; the a la carte menu is always shown.
    menus = [truck.defaultEventMenu, truck.defaultCateringMenu].filter(
      (m) => m && m.active && (!m.catering || m.public) && m.items.length > 0
    ) as TruckMenuContentFragment[]
  }

  const menuTabs = sortBy(
    menus.map((menu) => ({
      name: menu.catering ? 'Catering' : 'A La Carte',
      value: menu.id
    })),
    (t) => (t.name === 'A La Carte' ? 0 : 1)
  )
  if (menuTabs.length === 0) return <>{fallback}</>

  const menu = menus.find((m) => m.id === (currentTab || menuTabs[0]).value)
  if (!menu) return <>{fallback}</>

  const items = itemIds
    ? menu.items.filter((item) => itemIds.includes(item.id))
    : menu.items

  return (
    <div className="max-w-screen-lg mx-auto mt-10" id="menu">
      <p className="text-3xl text-center font-bold mb-6">{`${truck.name} Menu`}</p>
      {describeMenuPricing && (
        <p className="text-center text-gray-500 mb-6 max-w-xl mx-auto">
          {menu.catering
            ? 'This is a catering menu. Pricing is based on the number of food items per guest and total guest count, subject to a minimum amount.'
            : "Each item is individually priced. Guests will be paying themselves for each item they order. Once an event is confirmed it's possible for guests to pre-order in advance. Subject to a minimum orders amount."}
        </p>
      )}
      {menus.length > 1 && (
        <div>
          <Tabs
            color="primary"
            currentTab={currentTab || menuTabs[0]}
            setCurrentTab={setCurrentTab}
            size="medium"
            tabs={menuTabs}
            variant="contained"
          />
        </div>
      )}
      <TruckMenu
        items={items}
        primaryUpsellTypes={truck.primaryUpsellTypes}
        showPrices={menu.hasPrices}
        taxIncluded={menu.taxIncluded}
        upsellTypes={data.upsellTypes}
      />
      {showLinkToSchedule && (
        <div className="my-10 flex justify-center">
          <ButtonLink
            color="primary"
            href={pathFromUrl(truck.scheduleUrl)}
            size="large"
            variant="contained"
            shadow
          >
            View Schedule
          </ButtonLink>
        </div>
      )}
    </div>
  )
}

export default TruckMenus
